// src/composables/useCookies.ts

import { ref, computed, onMounted } from 'vue';
import cookieService, { CookiePreferences } from '@/services/cookieService';

export function useCookies() {
  const showBanner = ref(false);
  const preferences = ref<CookiePreferences | null>(null);
  const hasConsented = ref(false);

  const hasConsent = computed(() => cookieService.hasConsent());

  const getPreferences = () => {
    return cookieService.getPreferences();
  };

  const acceptAll = (): void => {
    const allPreferences: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    cookieService.setPreferences(allPreferences);
    preferences.value = allPreferences;
    hasConsented.value = true;
    showBanner.value = false;
  };

  const refuseAll = (): void => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    cookieService.setPreferences(necessaryOnly);
    preferences.value = necessaryOnly;
    hasConsented.value = true;
    showBanner.value = false;
    cookieService.clearNonNecessaryCookies();
  };

  const setPreferences = (newPreferences: CookiePreferences): void => {
    cookieService.setPreferences(newPreferences);
    preferences.value = newPreferences;
    hasConsented.value = true;
    showBanner.value = false;
  };

  const isAllowed = (category: keyof CookiePreferences): boolean => {
    return cookieService.isCategoryAllowed(category);
  };

  const reset = (): void => {
    cookieService.resetPreferences();
    preferences.value = null;
    hasConsented.value = false;
    showBanner.value = true;
  };

  const init = (): void => {
    hasConsented.value = cookieService.hasConsent();
    preferences.value = cookieService.getPreferences();
    if (!hasConsented.value) {
      setTimeout(() => {
        showBanner.value = true;
      }, 500);
    }
  };

  onMounted(() => {
    init();
  });

  return {
    showBanner,
    preferences,
    hasConsented,
    hasConsent,
    acceptAll,
    refuseAll,
    setPreferences,
    isAllowed,
    reset,
    init
  };
}